import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, Unlink } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AutomationSettingsLinkAlert } from '@/components/settings/AutomationSettingsLinkAlert';
import { AUTOMATION_TEMPLATE_UI } from '@/components/settings/automationTemplatesUi';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/hooks/use-auth';
import { CampaignLinkDialog, type CampaignLinkRecord } from './CampaignLinkDialog';

interface AdAccount {
    account_id: string;
    name: string;
    account_status: number;
    currency: string;
}

interface Campaign {
    id: string;
    name: string;
    status: string;
    objective: string;
}

interface CampaignLink extends CampaignLinkRecord {
    campaign_name?: string;
    ad_account_id?: string;
    is_active: boolean;
}

export function FacebookCampaignsList() {
    const { t } = useTranslation();
    const { lang } = useParams<{ lang: string }>();
    const { token } = useAuth();
    const [adAccounts, setAdAccounts] = useState<AdAccount[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<string>('');
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [needsAuth, setNeedsAuth] = useState(false);

    const [existingLinks, setExistingLinks] = useState<Map<string, CampaignLink>>(new Map());
    const [configuringCampaign, setConfiguringCampaign] = useState<Campaign | null>(null);
    const [configuringLink, setConfiguringLink] = useState<CampaignLink | null>(null);
    const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
    const [togglingCampaignId, setTogglingCampaignId] = useState<string | null>(null);

    const fetchExistingLinks = React.useCallback(async () => {
        try {
            const response = await fetch('/api/v1/leads/meta/links/', {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.ok) {
                const links: CampaignLink[] = await response.json();
                const linksMap = new Map<string, CampaignLink>();
                links.forEach((link) => linksMap.set(link.campaign_id, link));
                setExistingLinks(linksMap);
            }
        } catch (err) {
            console.error('Failed to fetch existing links', err);
        }
    }, [token]);

    useEffect(() => {
        const fetchAdAccounts = async () => {
            setLoadingAccounts(true);
            setError(null);
            setNeedsAuth(false);
            try {
                const response = await fetch('/api/v1/leads/meta/ad-accounts/', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    let errorMessage = '';
                    try {
                        const errorData = await response.json();
                        errorMessage = errorData?.error || '';
                    } catch {
                        if (response.status === 400) {
                            errorMessage = 'authentication required';
                        }
                    }

                    if (
                        errorMessage.toLowerCase().includes('token') ||
                        errorMessage.toLowerCase().includes('connect') ||
                        errorMessage.toLowerCase().includes('facebook account') ||
                        errorMessage.toLowerCase().includes('authentication required')
                    ) {
                        setNeedsAuth(true);
                        setError(null);
                        return;
                    }

                    throw new Error(t('actions.campaign_links.fetch_error'));
                }

                const data = await response.json();
                setAdAccounts(data.data || []);

                if (data.data && data.data.length > 0) {
                    setSelectedAccount(data.data[0].account_id);
                }
            } catch (err) {
                if (!needsAuth) {
                    setError(err instanceof Error ? err.message : t('error.generic'));
                }
            } finally {
                setLoadingAccounts(false);
            }
        };

        if (token) {
            fetchAdAccounts();
            fetchExistingLinks();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, fetchExistingLinks, t]);

    useEffect(() => {
        const fetchCampaigns = async () => {
            if (!selectedAccount) return;

            setLoadingCampaigns(true);
            try {
                const response = await fetch(`/api/v1/leads/meta/campaigns/?ad_account_id=${selectedAccount}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) throw new Error('Failed to fetch campaigns');

                const data = await response.json();
                setCampaigns(data.data || []);
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingCampaigns(false);
            }
        };

        fetchCampaigns();
    }, [selectedAccount, token]);

    const openConfigureDialog = (campaign: Campaign, link: CampaignLink | null) => {
        setConfiguringCampaign(campaign);
        setConfiguringLink(link);
        setIsConfigDialogOpen(true);
    };

    const handleConfigSuccess = () => {
        fetchExistingLinks();
    };

    const handleUnlink = async (campaignId: string) => {
        if (!confirm('¿Quitar la plantilla de esta campaña?')) return;

        try {
            const response = await fetch(`/api/v1/leads/meta/links/?campaign_id=${campaignId}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                fetchExistingLinks();
            } else {
                throw new Error('Failed to unlink');
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleToggleActivation = async (campaignId: string, isActive: boolean) => {
        setTogglingCampaignId(campaignId);
        setError(null);
        try {
            const response = await fetch(`/api/v1/leads/meta/links/?campaign_id=${campaignId}`, {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ is_active: isActive }),
            });

            if (!response.ok) {
                throw new Error('Failed to update campaign automation status');
            }

            await fetchExistingLinks();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update campaign automation status');
        } finally {
            setTogglingCampaignId(null);
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex justify-between items-center">
                    Campañas de Facebook
                    {loadingAccounts ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {needsAuth && lang ? (
                    <AutomationSettingsLinkAlert
                        lang={lang}
                        message={AUTOMATION_TEMPLATE_UI.facebookRequiredMessage}
                    />
                ) : null}
                {error && !needsAuth ? (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                ) : null}

                <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">Cuenta publicitaria:</span>
                    <Select value={selectedAccount} onValueChange={setSelectedAccount} disabled={loadingAccounts}>
                        <SelectTrigger className="w-[280px]">
                            <SelectValue placeholder="Seleccionar cuenta" />
                        </SelectTrigger>
                        <SelectContent>
                            {adAccounts.map((account) => (
                                <SelectItem key={account.account_id} value={account.account_id}>
                                    {account.name} ({account.account_id})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Campaña</TableHead>
                                <TableHead>Estado Meta</TableHead>
                                <TableHead>Plantilla</TableHead>
                                <TableHead>Automatización</TableHead>
                                <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loadingCampaigns ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-4">
                                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                                    </TableCell>
                                </TableRow>
                            ) : campaigns.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-4 text-muted-foreground">
                                        No hay campañas en esta cuenta.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                campaigns.map((campaign) => {
                                    const link = existingLinks.get(campaign.id);
                                    return (
                                        <TableRow key={campaign.id}>
                                            <TableCell className="font-medium">
                                                {campaign.name}
                                                <div className="text-xs text-muted-foreground">{campaign.id}</div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={campaign.status === 'ACTIVE' ? 'default' : 'secondary'}>
                                                    {campaign.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {link ? (
                                                    <span className="text-sm">
                                                        {link.template_name}
                                                        <span className="text-muted-foreground"> ({link.template_language})</span>
                                                    </span>
                                                ) : (
                                                    <span className="text-muted-foreground text-sm">Sin configurar</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {link ? (
                                                    <div className="flex items-center gap-2">
                                                        <Switch
                                                            checked={link.is_active}
                                                            onCheckedChange={(checked) =>
                                                                void handleToggleActivation(campaign.id, checked)
                                                            }
                                                            disabled={togglingCampaignId === campaign.id}
                                                            aria-label={`Activar automatización ${campaign.name}`}
                                                        />
                                                        <span className="text-xs text-muted-foreground">
                                                            {link.is_active ? 'Activa' : 'Pausada'}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted-foreground text-sm">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => openConfigureDialog(campaign, link ?? null)}
                                                    >
                                                        {link
                                                            ? AUTOMATION_TEMPLATE_UI.editTemplateButton
                                                            : 'Configurar plantilla'}
                                                    </Button>
                                                    {link ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon-xs"
                                                            className="text-destructive"
                                                            onClick={() => handleUnlink(campaign.id)}
                                                            title="Quitar plantilla"
                                                        >
                                                            <Unlink className="h-4 w-4" />
                                                        </Button>
                                                    ) : null}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>

            <CampaignLinkDialog
                campaign={configuringCampaign}
                adAccountId={selectedAccount}
                existingLink={configuringLink}
                isOpen={isConfigDialogOpen}
                onClose={() => {
                    setIsConfigDialogOpen(false);
                    setConfiguringCampaign(null);
                    setConfiguringLink(null);
                }}
                onLinkSuccess={handleConfigSuccess}
            />
        </Card>
    );
}
